import { useState, useEffect, useRef } from 'react'
import { InputLabel, Stack, TextField, Select, MenuItem, FormControl } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { useEditMode } from '../../../contexts/edit-mode.context'
import {
  ComponentContainer,
  DemoContainer,
} from '../date-range-picker-field/date-range-picker-field.styles'
import styled from 'styled-components'

const TimeInputContainer = styled.div<{ $readOnly?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
  min-height: 40px;
  height: 40px;
  box-sizing: border-box;
  background-color: ${({ $readOnly }) => ($readOnly ? '#F0F2F5' : 'white')};
  border: ${({ $readOnly }) => ($readOnly ? 'none' : '0.1px solid #ccc')};
  border-radius: ${({ $readOnly }) => ($readOnly ? '8px' : '10px')};
  padding: 0 8px;
`

const TimeBox = styled(TextField)<{ $readOnly?: boolean }>`
  flex: 1;
  min-width: 0;
  
  & .MuiOutlinedInput-root {
    height: 36px;
    & fieldset {
      border: none;
    }
    & input {
      text-align: center;
      padding: 4px 2px;
      font-size: 0.875rem;
      color: #000000;
    }
    & input:disabled {
      -webkit-text-fill-color: #000000;
      color: #000000;
    }
  }
`

const AMPMSelect = styled(Select)<{ $hideIcon?: boolean; $readOnly?: boolean }>`
  flex: 1;
  min-width: 0;
  height: 36px;
  
  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }
  
  & .MuiSelect-select {
    text-align: center;
    padding: 4px 2px !important;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000000;
  }
  
  & .MuiSelect-select.Mui-disabled {
    -webkit-text-fill-color: #000000;
    color: #000000;
  }
  
  & .MuiSelect-icon {
    display: ${({ $hideIcon }) => ($hideIcon ? 'none' : 'inherit')};
  }
`

const Separator = styled.span`
  color: #666;
  font-weight: 500;
`

interface TimeInputFieldProps {
  label: string
  name: string
  highlight?: boolean
  bold?: boolean
  disabled?: boolean
  hideSelectIcon?: boolean
}

const TimeInputField = ({
  label,
  name,
  highlight,
  bold,
  disabled,
  hideSelectIcon,
}: TimeInputFieldProps) => {
  const { control, setValue, watch } = useFormContext()
  const { isEditMode, hasContext } = useEditMode()
  const isDisabled = disabled !== undefined 
    ? disabled 
    : (hasContext ? !isEditMode : false)
  const isViewMode = hasContext && !isEditMode
  const showHighlight = Boolean(highlight) && !isViewMode

  // Watch the form value to extract HH, MM, AM/PM
  const fieldValue = watch(name)

  // Parse the time value into HH, MM, AM/PM
  const parseTime = (value: string | null | undefined) => {
    if (!value) return { hh: '', mm: '', ampm: 'AM' }
    
    // If it's a time string like "12:30 PM"
    const timeMatch = value.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i)
    if (timeMatch) {
      return {
        hh: timeMatch[1],
        mm: timeMatch[2],
        ampm: (timeMatch[3] || 'AM').toUpperCase(),
      }
    }
    
    
    // If it's a datetime string, extract time
    try {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        const hours = date.getHours()
        const minutes = date.getMinutes()
        const hh12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
        return {
          hh: hh12.toString().padStart(2, '0'),
          mm: minutes.toString().padStart(2, '0'),
          ampm: hours >= 12 ? 'PM' : 'AM',
        }
      }
    } catch (e) {
      // Invalid date
    }
    
    return { hh: '', mm: '', ampm: 'AM' }
  }

  const [timeParts, setTimeParts] = useState(() => parseTime(fieldValue))
  const isUserTyping = useRef(false)

  // Update time parts when field value changes (but not when user is typing)
  useEffect(() => {
    if (!isUserTyping.current) {
      setTimeParts(parseTime(fieldValue))
    }
  }, [fieldValue])

  // Combine time parts and update form value
  const updateFormValue = (hh: string, mm: string, ampm: string) => {
    isUserTyping.current = true
    if (hh && mm) {
      const formattedTime = `${hh}:${mm} ${ampm}`
      setValue(name, formattedTime, { shouldValidate: true })
    } else if (hh || mm || ampm) {
      // Partial input, store as is for better UX
      let partialTime = ''
      if (hh) partialTime = hh
      if (mm && hh) partialTime = `${hh}:${mm}`
      if (ampm && (hh || mm)) partialTime = `${partialTime || hh || mm} ${ampm}`
      setValue(name, partialTime, { shouldValidate: true })
    } else {
      setValue(name, '', { shouldValidate: true })
    }
    setTimeout(() => {
      isUserTyping.current = false
    }, 100)
  }

  const handleHHChange = (value: string) => {
    // Only allow digits
    const digits = value.replace(/[^0-9]/g, '')
    let hh = digits
    
    // Don't pad if empty - allow user to delete and type fresh
    if (hh && hh.length === 1 && digits.length === 1) {
      // Only pad on blur or when user completes typing, not while typing
      // For now, allow single digits while typing
    }
    
    // Validate: only change to 12 if greater than 12 (allow 01-12, including 11)
    if (hh && hh.length === 2) {
      const num = parseInt(hh, 10)
      if (num > 12) {
        hh = '12'
      }
    }
    
    const newTimeParts = { ...timeParts, hh }
    setTimeParts(newTimeParts)
    updateFormValue(newTimeParts.hh, newTimeParts.mm, newTimeParts.ampm)
  }

  const handleMMChange = (value: string) => {
    // Only allow digits
    const digits = value.replace(/[^0-9]/g, '')
    let mm = digits
    
    // Don't pad if empty - allow user to delete and type fresh
    if (mm && mm.length === 1 && digits.length === 1) {
      // Only pad on blur or when user completes typing, not while typing
      // For now, allow single digits while typing
    }
    
    // Validate: max 59, if greater than 59, keep as 00
    if (mm && mm.length === 2) {
      const num = parseInt(mm, 10)
      if (num > 59) {
        mm = '00'
      }
    }
    
    // Keep the existing HH value
    const newTimeParts = { ...timeParts, mm }
    setTimeParts(newTimeParts)
    updateFormValue(newTimeParts.hh, newTimeParts.mm, newTimeParts.ampm)
  }

  const handleAMPMChange = (value: string) => {
    const newTimeParts = { ...timeParts, ampm: value }
    setTimeParts(newTimeParts)
    updateFormValue(newTimeParts.hh, newTimeParts.mm, newTimeParts.ampm)
  }

  const handleHHBlur = () => {
    // Pad single digit to 2 digits on blur (e.g., 7 -> 07)
    if (timeParts.hh && timeParts.hh.length === 1) {
      const padded = timeParts.hh.padStart(2, '0')
      const newTimeParts = { ...timeParts, hh: padded }
      setTimeParts(newTimeParts)
      updateFormValue(newTimeParts.hh, newTimeParts.mm, newTimeParts.ampm)
    }
  }

  const handleMMBlur = () => {
    // Pad single digit to 2 digits on blur (e.g., 7 -> 07)
    if (timeParts.mm && timeParts.mm.length === 1) {
      const padded = timeParts.mm.padStart(2, '0')
      const newTimeParts = { ...timeParts, mm: padded }
      setTimeParts(newTimeParts)
      updateFormValue(newTimeParts.hh, newTimeParts.mm, newTimeParts.ampm)
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ fieldState: { error } }) => (
        <>
          <InputLabel
            sx={{
              color: showHighlight ? 'error.main' : 'secondary.dark',
              fontWeight: showHighlight || bold ? 700 : isViewMode ? 500 : 300,
              ...(isViewMode && {
                color: 'text.secondary',
                fontSize: '0.875rem',
              }),
            }}
          >
            <Stack>{label}</Stack>
          </InputLabel>
          <DemoContainer>
            <ComponentContainer>
              <TimeInputContainer $readOnly={isViewMode}>
                <TimeBox
                  variant="outlined"
                  placeholder="HH"
                  value={timeParts.hh}
                  onChange={(e) => handleHHChange(e.target.value)}
                  onBlur={handleHHBlur}
                  disabled={isDisabled}
                  $readOnly={isViewMode}
                  inputProps={{
                    maxLength: 2,
                    pattern: '[0-9]*',
                  }}
                  size="small"
                />
                <Separator>:</Separator>
                <TimeBox
                  variant="outlined"
                  placeholder="MM"
                  value={timeParts.mm}
                  onChange={(e) => handleMMChange(e.target.value)}
                  onBlur={handleMMBlur}
                  disabled={isDisabled}
                  $readOnly={isViewMode}
                  inputProps={{
                    maxLength: 2,
                    pattern: '[0-9]*',
                  }}
                  size="small"
                />
                <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                  <AMPMSelect
                    $hideIcon={hideSelectIcon || isViewMode}
                    $readOnly={isViewMode}
                    IconComponent={hideSelectIcon || isViewMode ? () => null : undefined}
                    value={timeParts.ampm}
                    onChange={(e) => handleAMPMChange(e.target.value as string)}
                    disabled={isDisabled}
                    displayEmpty
                  >
                    <MenuItem value="AM">AM</MenuItem>
                    <MenuItem value="PM">PM</MenuItem>
                  </AMPMSelect>
                </FormControl>
              </TimeInputContainer>
              {error && <span style={{ color: 'error.main', fontSize: '0.75rem' }}>{error.message}</span>}
            </ComponentContainer>
          </DemoContainer>
        </>
      )}
    />
  )
}

export default TimeInputField

